import { useCallback, useEffect, useRef, useState } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./TeamRoster.module.css";

/* ── On-court player numbers (top 5 starters) ── */
const ON_COURT_NUMBERS = new Set([2, 7, 11, 13, 14]);

/* ── Live state per player ── */
interface LiveState {
  bpm: number;
  loadStatus: "green" | "yellow" | "red";
}

/* Randomize BPM within range per tick */
function jitterBpm(base: number, isOnCourt: boolean): number {
  const range = isOnCourt ? 12 : 4;
  return base + Math.round((Math.random() - 0.5) * range);
}

/* Occasionally flip load status for realism */
function jitterLoad(
  current: "green" | "yellow" | "red",
  isOnCourt: boolean,
): "green" | "yellow" | "red" {
  if (Math.random() > 0.06) return current; // 6% chance per tick
  if (isOnCourt) {
    // on-court: may escalate
    if (current === "green") return "yellow";
    if (current === "yellow") return Math.random() > 0.5 ? "red" : "green";
    return "yellow";
  }
  // bench: tends toward green
  if (current === "yellow") return "green";
  return current;
}

const LOAD_CLASS: Record<string, string> = {
  green: styles.loadGreen,
  yellow: styles.loadYellow,
  red: styles.loadRed,
};

export function TeamRoster() {
  const { t } = useI18n();

  const loadLabel: Record<string, string> = {
    green: t.aiTeam.rosterLoadReady,
    yellow: t.aiTeam.rosterLoadMonitor,
    red: t.aiTeam.rosterLoadFatigued,
  };
  const reducedMotion = useReducedMotion();
  const players = t.aiTeam.rosterPlayers;

  /* Live states — initialized from i18n data */
  const [liveStates, setLiveStates] = useState<LiveState[]>(() =>
    players.map((p) => ({ bpm: p.hrAvg, loadStatus: p.loadStatus })),
  );

  /* Tick every 1s — jitter BPMs and occasionally flip status */
  const baseRef = useRef(players);
  baseRef.current = players;

  const tick = useCallback(() => {
    setLiveStates((prev) =>
      prev.map((state, i) => {
        const base = baseRef.current[i];
        const onCourt = ON_COURT_NUMBERS.has(base.number);
        return {
          bpm: jitterBpm(base.hrAvg, onCourt),
          loadStatus: jitterLoad(state.loadStatus, onCourt),
        };
      }),
    );
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tick, reducedMotion]);

  /* GSAP entrance */
  const ref = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const cards = el.querySelectorAll("[data-roster-card]");
    gsap.set(cards, { y: 30, opacity: 0 });
    gsap.to(cards, {
      y: 0,
      opacity: 1,
      duration: 0.5,
      stagger: 0.04,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  return (
    <section ref={ref} className={styles.section}>
      <h2 className={styles.title}>{t.aiTeam.rosterTitle}</h2>
      <p className={styles.subtitle}>{t.aiTeam.rosterSubtitle}</p>

      {/* Legend */}
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: "var(--color-ai-primary)" }} />
          {t.aiTeam.rosterLegendOnCourt}
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: "var(--color-gray-600)" }} />
          {t.aiTeam.rosterLegendBench}
        </span>
      </div>

      <div className={styles.grid}>
        {players.map((player, i) => {
          const onCourt = ON_COURT_NUMBERS.has(player.number);
          const live = liveStates[i];

          return (
            <div
              key={player.number}
              className={`${styles.card} ${onCourt ? styles.cardOnCourt : styles.cardBench}`}
              data-roster-card
            >
              {/* On-court badge */}
              {onCourt && (
                <span className={styles.onCourtBadge}>{t.aiTeam.rosterOnCourtBadge}</span>
              )}

              <span className={styles.number}>#{player.number}</span>
              <span className={styles.name}>{player.name}</span>
              <span className={styles.meta}>
                {player.pos} · {player.height}
              </span>

              {/* Live BPM with pulse ring */}
              <div className={`${styles.hrRow} ${onCourt ? styles.hrRowActive : ""}`}>
                <span
                  className={styles.hrPulse}
                  style={{ animationDuration: onCourt ? "0.6s" : "1.2s" }}
                />
                <span
                  className={styles.hrValue}
                  style={{ fontSize: onCourt ? "1.1rem" : undefined }}
                >
                  {live.bpm}
                </span>
                <span className={styles.hrUnit}>BPM</span>
              </div>

              {/* Mini HR bar (visual intensity) */}
              <div className={styles.hrBar}>
                <div
                  className={styles.hrBarFill}
                  style={{
                    width: `${Math.min(100, ((live.bpm - 60) / 120) * 100)}%`,
                    background: live.bpm > 150 ? "#ff3b30" : live.bpm > 130 ? "#ffcc00" : "#34c759",
                  }}
                />
              </div>

              {/* Load status */}
              <div className={styles.loadRow}>
                <span className={`${styles.loadDot} ${LOAD_CLASS[live.loadStatus]}`} />
                <span className={styles.loadLabel}>{loadLabel[live.loadStatus]}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
